// KeyboardShortcutsOverlay — global help overlay listing every reachable
// keyboard shortcut in Studio. Mounted once at the EditorApp root; opens on
// `?` (Shift+/) and closes on Escape, backdrop click, or the × button.
//
// View-only. The overlay does NOT implement the shortcuts — those are owned
// by their respective surfaces (canvas, clipboard, timeline, scene list).
// This component is the canonical place to *discover* them.
//
// Why a global keydown listener instead of binding inside EditorApp.jsx:
//   - EditorApp is already 4500+ LOC and packed with keyboard concerns.
//   - The overlay is the only consumer of `?`; keeping the listener local
//     means turning the feature off is a one-line removal from JSX.
//   - The listener bails out when focus is inside an editable surface
//     (input/textarea/contentEditable) so typing `?` into a text field
//     never opens the overlay.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import "./KeyboardShortcutsOverlay.scss";

// Categories of shortcuts. Each row is { keys, label, anyOf? }. `keys` is an
// array of strings rendered as separate kbd chips joined by "+" — or by "/"
// when `anyOf` is set (either key works). Adding a shortcut = append a row.
//
// Every row maps to a live handler — keep it that way (no aspirational or
// hidden-feature entries):
//   - Undo/redo, Ctrl+A, arrows, Delete/Backspace, Esc (multi-box), F2,
//     Ctrl+Shift+P → EditorApp.keyListener
//   - Copy/cut/paste → Clipboard's native copy/cut/paste listeners;
//     Ctrl+Alt+E → Clipboard.duplicateListener
//   - Delete on a timeline clip → IdeaTimeline keydown listener
//   - Modifier+click on boxes and Shift while resizing → EditorBox
//   - Scene list modifier+click and Esc → FileTree
// We do NOT show plain "click" or "drag" actions — only keys and
// modifier+click combos.

// macOS uses ⌘ where Windows/Linux use Ctrl. Only rows whose handler accepts
// metaKey use `mod`; handlers that check ctrlKey alone (Ctrl+Y, Ctrl+Alt+E,
// Ctrl+Shift+P) always show "Ctrl".
function isMacPlatform() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || "");
}

export function getSections() {
  const mod = isMacPlatform() ? "⌘" : "Ctrl";
  const click = i18n.t("shortcuts.click", { defaultValue: "Click" });
  return [
    {
      id: "general",
      title: i18n.t("shortcuts.section_general", { defaultValue: "General" }),
      rows: [
        {
          keys: ["?"],
          label: i18n.t("shortcuts.help_open", { defaultValue: "Show or hide this list" }),
        },
        {
          keys: ["Esc"],
          label: i18n.t("shortcuts.esc_close", {
            defaultValue: "Close the open dialog, panel or menu",
          }),
        },
        {
          keys: ["F2"],
          label: i18n.t("shortcuts.preview", { defaultValue: "Preview the course" }),
        },
        {
          keys: ["Ctrl", "Shift", "P"],
          label: i18n.t("shortcuts.print_pdf", {
            defaultValue: "Print the course or save it as PDF",
          }),
        },
      ],
    },
    {
      id: "editing",
      title: i18n.t("shortcuts.section_editing", { defaultValue: "Editing" }),
      rows: [
        {
          keys: [mod, "Z"],
          label: i18n.t("shortcuts.undo", { defaultValue: "Undo" }),
        },
        {
          keys: [mod, "Shift", "Z"],
          label: i18n.t("shortcuts.redo", { defaultValue: "Redo" }),
        },
        {
          keys: ["Ctrl", "Y"],
          label: i18n.t("shortcuts.redo_alt", { defaultValue: "Redo (alternative)" }),
        },
        {
          keys: [mod, "C"],
          label: i18n.t("shortcuts.copy", {
            defaultValue: "Copy the selected element (with nothing selected, copy the whole scene)",
          }),
        },
        {
          keys: [mod, "X"],
          label: i18n.t("shortcuts.cut", { defaultValue: "Cut the selected element" }),
        },
        {
          keys: [mod, "V"],
          label: i18n.t("shortcuts.paste", {
            defaultValue: "Paste a copied element or scene, or an image or text from the clipboard",
          }),
        },
        {
          keys: ["Ctrl", "Alt", "E"],
          label: i18n.t("shortcuts.duplicate", { defaultValue: "Duplicate the selected element" }),
        },
        {
          keys: ["Delete", "Backspace"],
          anyOf: true,
          label: i18n.t("shortcuts.delete_box", {
            defaultValue: "Delete the selected element(s), or the selected clip on the timeline",
          }),
        },
      ],
    },
    {
      id: "canvas",
      title: i18n.t("shortcuts.section_canvas", { defaultValue: "Selection & arrangement" }),
      rows: [
        {
          keys: [mod, "A"],
          label: i18n.t("shortcuts.select_all", {
            defaultValue: "Select every element on the current scene",
          }),
        },
        {
          keys: [mod, click],
          label: i18n.t("shortcuts.multi_select", {
            defaultValue: "Add or remove an element from the selection (Shift + click works too)",
          }),
        },
        {
          keys: ["Esc"],
          label: i18n.t("shortcuts.clear_selection", {
            defaultValue: "Clear a multi-element selection",
          }),
        },
        {
          keys: ["← ↑ → ↓"],
          label: i18n.t("shortcuts.move_box", {
            defaultValue: "Nudge the selected element (1% of the scene per press)",
          }),
        },
        {
          keys: ["Shift"],
          label: i18n.t("shortcuts.resize_aspect", {
            defaultValue: "Hold while resizing an element to lock or unlock its aspect ratio",
          }),
        },
      ],
    },
    {
      id: "scenes",
      title: i18n.t("shortcuts.section_scenes", { defaultValue: "Scene list" }),
      rows: [
        {
          keys: [mod, click],
          label: i18n.t("shortcuts.scenes_toggle_select", {
            defaultValue: "Add or remove a scene from the selection",
          }),
        },
        {
          keys: ["Shift", click],
          label: i18n.t("shortcuts.scenes_range_select", {
            defaultValue: "Select every scene between the last clicked one and this one",
          }),
        },
        {
          keys: ["Esc"],
          label: i18n.t("shortcuts.scenes_clear_select", {
            defaultValue: "Clear the scene selection",
          }),
        },
      ],
    },
  ];
}

// True when the keydown originated from a surface where the user is
// actively typing. We don't want `?` (Shift+/) to hijack the overlay
// when authors are writing prompt text into an MCQ option, etc.
function isTypingTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  // jsdom (and some legacy browsers) don't always populate isContentEditable
  // from the attribute alone — fall back to reading the attribute directly so
  // the test harness and SSR-hydrated DOMs behave identically.
  if (typeof target.getAttribute === "function") {
    const attr = target.getAttribute("contenteditable");
    if (attr === "" || attr === "true") return true;
  }
  return false;
}

function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);
  // Track whether the listener is currently bound. React strict-mode mounts
  // effects twice in dev, so we use a ref to make the bind/unbind idempotent.
  const boundRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (boundRef.current) return undefined;
    boundRef.current = true;

    const onKeyDown = (e) => {
      // Esc — close if open. Never opens.
      if (e.key === "Escape") {
        if (open) {
          setOpen(false);
          e.stopPropagation();
        }
        return;
      }
      // `?` opens the overlay. Skip if focus is in an editable surface,
      // or any modifier is held (Ctrl+?, Alt+?, etc. are reserved).
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (isTypingTarget(e.target)) return;
        setOpen((prev) => !prev);
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      boundRef.current = false;
    };
  }, [open]);

  // When the overlay is closed, render a small floating ? trigger button
  // just above the bottom-right assistant/widget launcher so the keyboard
  // shortcut is discoverable. Clicking it opens the overlay; the global `?`
  // keypress still works in parallel.
  if (!open) {
    const trigger = (
      <button
        type="button"
        className="keyboard-shortcuts-overlay__trigger"
        onClick={() => setOpen(true)}
        aria-label={i18n.t("shortcuts.trigger_label", {
          defaultValue: "Keyboard shortcuts (press ?)",
        })}
        title={i18n.t("shortcuts.trigger_label", {
          defaultValue: "Keyboard shortcuts (press ?)",
        })}
        data-testid="keyboard-shortcuts-overlay-trigger"
      >
        <span aria-hidden="true">?</span>
      </button>
    );
    return typeof document !== "undefined" ? createPortal(trigger, document.body) : trigger;
  }

  const sections = getSections();

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  const overlay = (
    <div
      className="keyboard-shortcuts-overlay__backdrop"
      onMouseDown={handleBackdrop}
      role="presentation"
    >
      <div
        className="keyboard-shortcuts-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyboard-shortcuts-overlay-title"
        data-testid="keyboard-shortcuts-overlay"
      >
        <header className="keyboard-shortcuts-overlay__head">
          <div className="keyboard-shortcuts-overlay__title-row">
            <i className="material-icons keyboard-shortcuts-overlay__head-icon">keyboard</i>
            <h2 id="keyboard-shortcuts-overlay-title" className="keyboard-shortcuts-overlay__title">
              {i18n.t("shortcuts.title", { defaultValue: "Keyboard shortcuts" })}
            </h2>
          </div>
          <button
            type="button"
            className="keyboard-shortcuts-overlay__close"
            onClick={() => setOpen(false)}
            aria-label={i18n.t("close", { defaultValue: "Close" })}
            title={i18n.t("close", { defaultValue: "Close" })}
            data-testid="keyboard-shortcuts-overlay-close"
          >
            <i className="material-icons">close</i>
          </button>
        </header>

        <div className="keyboard-shortcuts-overlay__body">
          {sections.map((section) => (
            <section
              className="keyboard-shortcuts-overlay__section"
              key={section.id}
              data-section={section.id}
            >
              <h3 className="keyboard-shortcuts-overlay__section-title">{section.title}</h3>
              <ul className="keyboard-shortcuts-overlay__rows">
                {section.rows.map((row) => (
                  <li className="keyboard-shortcuts-overlay__row" key={row.label}>
                    <span className="keyboard-shortcuts-overlay__keys">
                      {row.keys.map((k, idx) => (
                        <React.Fragment key={`${row.label}-${idx}`}>
                          {idx > 0 ? (
                            <span
                              aria-hidden="true"
                              className="keyboard-shortcuts-overlay__keys-sep"
                            >
                              {row.anyOf ? "/" : "+"}
                            </span>
                          ) : null}
                          <kbd className="keyboard-shortcuts-overlay__kbd">{k}</kbd>
                        </React.Fragment>
                      ))}
                    </span>
                    <span className="keyboard-shortcuts-overlay__row-label">{row.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="keyboard-shortcuts-overlay__foot">
          <i className="material-icons">lightbulb_outline</i>
          <span>
            {i18n.t("shortcuts.foot_hint", {
              defaultValue: "Press ? any time to open or close this list.",
            })}
          </span>
        </footer>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(overlay, document.body) : overlay;
}

export default KeyboardShortcutsOverlay;
